import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "@companion-core/web/src/Assets/Styles/scss/jpcounter.scss";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Card, Divider } from "antd";
import { getCountersFullLiveSummary } from "@companion-core/shared/app/Components/Inputs/JPInputCounters/JPInputCounters.model";
import { JPCounterProps } from "./index";

export const JPCounter = ({ testID, counters, onChange, value }: JPCounterProps) => {
  const { t } = useTranslation();
  const nbOfCounters = useMemo(() => counters.length, [counters]);
  const [liveStatusText, setLiveStatusText] = useState("");
  const skipFirstLiveAnnounce = useRef(true);
  const countersDefRef = useRef(counters);
  countersDefRef.current = counters;
  const tRef = useRef(t);
  tRef.current = t;
  const [countersState, setCountersState] = useState<Record<string, number>>(
    value ??
      counters.reduce(
        (acc, { name, minimumValue, defaultValue }) => ({
          ...acc,
          [name]: defaultValue ?? (minimumValue ? minimumValue : 0), // If no default value is provided, set the minimum value
        }),
        {}
      )
  );

  useEffect(() => {
    if (!value) {
      if (onChange) onChange(countersState);
    }
  }, [value]);

  useEffect(() => {
    if (skipFirstLiveAnnounce.current) {
      skipFirstLiveAnnounce.current = false;
      return;
    }
    setLiveStatusText(
      getCountersFullLiveSummary(countersState, countersDefRef.current, tRef.current)
    );
  }, [countersState]);

  const setCount = (name: string, value: number) => {
    const newState = { ...countersState, [name]: value };
    setCountersState(newState);
    if (onChange) onChange(newState);
  };

  return (
    <div className="counter-input" data-testid={testID}>
      <Card className="counter-input__counter-card" variant={"borderless"}>
        {counters.map(({ name, title, subtitle, minimumValue, maximumValue }, index) => (
          <div key={name} data-testid={`counter-input__${name}`}>
            <Counter
              key={name}
              instanceId={name}
              title={title}
              subtitle={subtitle}
              count={countersState[name]}
              setCount={(value: number) => setCount(name, value)}
              minimumCount={minimumValue}
              maximumCount={maximumValue}
              increaseAriaLabel={t("common.counters.increase", {
                name: title,
                count: countersState[name],
              })}
              decreaseAriaLabel={t("common.counters.decrease", {
                name: title,
                count: countersState[name],
              })}
            />
            {index < nbOfCounters - 1 && <Divider className="counter-input__line"></Divider>}
          </div>
        ))}
        <div role="status" aria-live="polite" aria-atomic="true" className="counter-input__sr-only">
          {liveStatusText}
        </div>
      </Card>
    </div>
  );
};

interface CounterProps {
  instanceId: string;
  title: string;
  subtitle?: string;
  minimumCount?: number;
  maximumCount?: number;
  count: number;
  setCount: (value: number) => void;
  increaseAriaLabel?: string;
  decreaseAriaLabel?: string;
}

const Counter = ({
  instanceId,
  title,
  subtitle,
  minimumCount = 0,
  maximumCount,
  count,
  setCount,
  increaseAriaLabel,
  decreaseAriaLabel,
}: CounterProps) => {
  const minusButtonDisabled = count <= minimumCount;
  const plusButtonDisabled = maximumCount ? count >= maximumCount : false;
  const labelId = `counter-${instanceId}-label`;
  const valueId = `counter-${instanceId}-value`;

  const handleOnClick = (number: number) => {
    if (maximumCount && (count + number > maximumCount || count + number < minimumCount)) {
      return;
    }
    setCount(count + number);
  };

  const handleKeyDown = (e: React.KeyboardEvent, number: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOnClick(number);
    }
  };

  const elements = document.querySelectorAll(".counter-input__custom-icon-button");
  elements.forEach((el) => {
    el.addEventListener("blur", () => {
      el.classList.remove("focus-visible");
    });
  });

  return (
    <div className="counter-input__container" role="group" aria-labelledby={labelId}>
      <div className="counter-input__title-container">
        <div id={labelId} className="body-text-5 counter-input__title-container-title">
          {title}
        </div>
        {subtitle && (
          <span className="body-text-8 counter-input__title-container-subtitle">{subtitle}</span>
        )}
      </div>
      <div className="counter-input__buttons-container">
        <button
          type="button"
          className={`counter-input__custom-icon-button ${
            minusButtonDisabled ? "counter-input__custom-icon-button--disabled" : ""
          }`}
          onClick={() => handleOnClick(-1)}
          onKeyDown={(e) => handleKeyDown(e, -1)}
          aria-label={decreaseAriaLabel}
          disabled={minusButtonDisabled}>
          <FontIcon name={"JP-Circle-minus-outline"} size={25} aria-hidden />
        </button>
        <span id={valueId} className="body-text-6 counter-input__counter" aria-hidden="true">
          {count}
        </span>
        <button
          type="button"
          className={`counter-input__custom-icon-button ${
            plusButtonDisabled ? "counter-input__custom-icon-button--disabled" : ""
          }`}
          onClick={() => handleOnClick(1)}
          onKeyDown={(e) => handleKeyDown(e, 1)}
          aria-label={increaseAriaLabel}
          disabled={plusButtonDisabled}>
          <FontIcon name={"JP-Circle-plus-outline"} size={25} aria-hidden />
        </button>
      </div>
    </div>
  );
};
